import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { HistoryFocus } from '../layout/focus';
import { auditActions, fetchAudit, listUsers, type AuditEntry, type AuditFilters } from './api';

const PAGE = 50;

function value(input: unknown): string {
  if (input === null || input === undefined) return '—';
  return String(input);
}

/**
 * One entry, expanded.
 *
 * The diff is the point. A row saying "component updated" answers nothing; the
 * question being asked of an audit log is always "what changed, and to what".
 */
function Details({ entry }: { entry: AuditEntry }) {
  const { t } = useTranslation();
  const changes = Object.entries(entry.changes ?? {});

  return (
    <div className="audit__details">
      {changes.length > 0 && (
        <table className="audit__diff">
          <thead>
            <tr>
              <th>{t('audit.field')}</th>
              <th>{t('audit.before')}</th>
              <th>{t('audit.after')}</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(([field, change]) => (
              <tr key={field}>
                <td>{field}</td>
                <td className="audit__old">{value(change.old)}</td>
                <td className="audit__new">{value(change.new)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {entry.context && (
        <pre className="audit__context">{JSON.stringify(entry.context, null, 2)}</pre>
      )}

      <dl className="audit__meta">
        <dt>{t('audit.address')}</dt>
        <dd>{entry.ip ?? '—'}</dd>
        <dt>{t('audit.entity')}</dt>
        <dd>
          {entry.entity_type ?? '—'}
          {entry.entity_id ? ` · ${entry.entity_id}` : ''}
        </dd>
        <dt>{t('audit.request')}</dt>
        <dd>{entry.request_id ?? '—'}</dd>
        <dt>{t('audit.agent')}</dt>
        <dd>{entry.user_agent ?? '—'}</dd>
      </dl>
    </div>
  );
}

export function AuditPage({ focus }: { focus?: HistoryFocus | null }) {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<AuditFilters>({ limit: PAGE, offset: 0 });
  const [expanded, setExpanded] = useState<number | null>(null);

  // Arriving from a record's own card: show that record's history and nothing
  // else. `seq` is in the dependency list so that asking twice for the same
  // record re-applies the filter after the user has widened it by hand.
  useEffect(() => {
    if (!focus) return;
    setFilters({
      limit: PAGE,
      offset: 0,
      entity_type: focus.entityType || undefined,
      entity_id: focus.id,
    });
  }, [focus]);

  const actions = useQuery({ queryKey: ['audit', 'actions'], queryFn: auditActions });
  const users = useQuery({ queryKey: ['users', false], queryFn: () => listUsers(false) });
  const page = useQuery({ queryKey: ['audit', filters], queryFn: () => fetchAudit(filters) });

  // Any change to a filter puts us back on the first page: staying on page four
  // of a different result set shows an empty screen that looks like a bug.
  const set = (patch: Partial<AuditFilters>) =>
    setFilters((current) => ({ ...current, ...patch, offset: 0 }));

  const total = page.data?.total ?? 0;
  const offset = filters.offset ?? 0;

  return (
    <div className="admin">
      <div className="admin__bar">
        <h2>{t('nav.audit')}</h2>
      </div>

      <div className="audit__filters">
        <label htmlFor="a-action">{t('audit.action')}</label>
        <select
          id="a-action"
          value={filters.action ?? ''}
          onChange={(e) => set({ action: e.target.value || undefined })}
        >
          <option value="">{t('audit.anyAction')}</option>
          {(actions.data ?? []).map((action) => (
            <option key={action} value={action}>
              {t(`audit.actions.${action}`, { defaultValue: action })}
            </option>
          ))}
        </select>

        <label htmlFor="a-actor">{t('audit.actor')}</label>
        <select
          id="a-actor"
          value={filters.actor_user_id ?? ''}
          onChange={(e) => set({ actor_user_id: e.target.value || undefined })}
        >
          <option value="">{t('audit.anyActor')}</option>
          {(users.data ?? []).map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name}
            </option>
          ))}
        </select>

        <label htmlFor="a-since">{t('audit.since')}</label>
        <input
          id="a-since"
          type="date"
          value={(filters.since ?? '').slice(0, 10)}
          onChange={(e) => set({ since: e.target.value ? `${e.target.value}T00:00:00` : undefined })}
        />

        <label htmlFor="a-entity">{t('audit.entityId')}</label>
        <input
          id="a-entity"
          value={filters.entity_id ?? ''}
          onChange={(e) => set({ entity_id: e.target.value || undefined })}
          placeholder={t('audit.entityIdHint')}
        />
      </div>

      <div className="scroll-x">
        <table className="admin__table audit__table">
          <thead>
            <tr>
              <th>{t('audit.at')}</th>
              <th>{t('audit.action')}</th>
              <th>{t('audit.actor')}</th>
              <th>{t('audit.entity')}</th>
            </tr>
          </thead>
          <tbody>
            {(page.data?.entries ?? []).map((entry) => (
              <Fragment key={entry.id}>
                <tr>
                  <td>{new Date(entry.at).toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                    >
                      {entry.action}
                    </button>
                  </td>
                  <td>{entry.actor_name ?? t('audit.system')}</td>
                  <td>{entry.entity_type ?? '—'}</td>
                </tr>
                {expanded === entry.id && (
                  <tr className="admin__detail-row">
                    <td colSpan={4}>
                      <Details entry={entry} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="audit__pager">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setFilters((c) => ({ ...c, offset: Math.max(0, offset - PAGE) }))}
        >
          {t('common.previous')}
        </button>
        <span>
          {t('audit.range', {
            from: total === 0 ? 0 : offset + 1,
            to: Math.min(offset + PAGE, total),
            total,
          })}
        </span>
        <button
          type="button"
          disabled={offset + PAGE >= total}
          onClick={() => setFilters((c) => ({ ...c, offset: offset + PAGE }))}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}
